import { motion } from "framer-motion";
import { ArrowRight, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface IntroProps {
  onBookConversation: () => void;
}

const metaKeys = ["trust2", "where", "contact"] as const;

const Intro = ({ onBookConversation }: IntroProps) => {
  const { t } = useTranslation();

  return (
    <section id="intro" className="bg-[#006B5F] py-10 sm:py-16">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto max-w-5xl rounded-[24px] border border-white/15 bg-white/5 px-4 py-8 text-center shadow-[0_20px_60px_rgba(0,0,0,0.08)] backdrop-blur-sm sm:rounded-[28px] sm:px-10 sm:py-10"
        >
          <img
            src="/images/omsorgsterapi-logo.png"
            alt="Omsorgsterapi Logo"
            className="mx-auto h-16 w-16 rounded-full sm:h-20 sm:w-20"
          />

          <h1 className="mt-4 text-3xl font-bold text-white sm:mt-5 sm:text-5xl">
            {t("intro.title")}
          </h1>

          <div className="mx-auto mt-4 max-w-4xl space-y-3 text-sm leading-6 text-white/90 sm:text-lg sm:leading-7">
            <p>{t("intro.headline")}</p>
            <p>{t("intro.subheadline")}</p>
          </div>

          <div className="mx-auto mt-6 flex max-w-3xl flex-col items-center justify-center gap-2 text-sm text-white/85 sm:flex-row sm:flex-wrap sm:gap-x-3 sm:gap-y-2">
            {metaKeys.map((key, index) => (
              <span key={key} className="flex items-center gap-3">
                <span>{t(`intro.meta.${key}`)}</span>
                {index < metaKeys.length - 1 && <span className="hidden text-white/40 sm:inline">•</span>}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              size="lg"
              onClick={onBookConversation}
              className="w-full bg-white text-[#006B5F] hover:bg-white/90 sm:w-auto"
            >
              {t("intro.primaryCta")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full border-white/25 bg-transparent text-white hover:bg-white/10 sm:w-auto"
            >
              <a href="mailto:info@omsorgsterapi.dk">{t("intro.secondaryCta")}</a>
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-sm text-white/85">
            <Mail className="h-4 w-4" />
            <a
              href="mailto:info@omsorgsterapi.dk"
              className="break-all underline underline-offset-4"
            >
              info@omsorgsterapi.dk
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Intro;
